import { useCallback, useEffect, useState } from 'react';
import { useTimer } from '../../../hooks';
import { ComponentSize, TextColor } from '../../../types';
import { ButtonColor, ButtonVariant, IconButton } from '../Button';
import { Icon, Icons } from '../Icons';
import { TextInput } from '../Input';

interface SearchBarProps {
  // The current submitted search value from the parent component.
  value: string;
  onSubmit: (newValue: string) => void;
}

const SearchBar = ({ value, onSubmit }: SearchBarProps) => {
  // The local search value that holds the search input value until the user submits it.
  const [searchInput, setSearchInput] = useState(value);

  const handleSubmit = useCallback(() => {
    // Only submit if the search input has changed from the current value to avoid unnecessary API calls.
    if (searchInput !== value) {
      onSubmit(searchInput);
    }
  }, [searchInput, value, onSubmit]);

  // Debounced timer to prevent excessive API calls while typing.
  const { startTimer } = useTimer({
    callback: handleSubmit,
    delay: 500,
  });

  // Auto-submit search after 500ms of no typing.
  useEffect(() => {
    startTimer();
  }, [searchInput, startTimer]);

  // If the parent component updates the search value, update the local state.
  // Handles the case where the user updates the search filter directly from the URL.
  useEffect(() => {
    if (value !== searchInput) {
      setSearchInput(value);
    }
    // We do not want to run this effect when searchInput changes.
  }, [value]);

  return (
    <TextInput
      placeholder="Search"
      endElement={
        searchInput && (
          <IconButton
            color={ButtonColor.NEUTRAL}
            icon={Icon.CLOSE}
            variant={ButtonVariant.GHOST}
            size={ComponentSize.X_SMALL}
            onClick={() => onSubmit('')}
          />
        )
      }
      startElement={
        !searchInput && (
          <div className="flex w-4 items-center justify-center">
            <Icons color={TextColor.SECONDARY} icon={Icon.SEARCH} size={ComponentSize.X_SMALL} />
          </div>
        )
      }
      value={searchInput}
      onBlur={handleSubmit} // Submit search when input loses focus to ensure search state stays in sync with user's intent.
      onChange={(e) => setSearchInput(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleSubmit();
        }
      }}
    />
  );
};

export default SearchBar;
